import Logger from "common/Logger"; const LOGGER = new Logger("protest.store.ts");/**
 * Protest Vuex Store
 *
 * @public
 */
import http, { HttpError, isHttpError } from "common/http"
import CompaniesDb from "components/companies/companies.db"
import { CompaniesSchemaWithId } from "components/companies/companies.schema"
import { AuthUrlRequest, AuthUrlResult } from "components/connections/connections"
import DailyDb from "components/daily/daily.db"
import WorklogsDb from "components/daily/worklogs/worklogs.db"
import HubDb from "components/hub/hub.db"
import { HubSchemaWithId } from "components/hub/hub.schema"
import AreasDb from "components/project/areas/areas.db"
import { AreasSchemaWithId } from "components/project/areas/areas.schema"
import favoriteprojectDb from "components/project/favoriteProjects/favoriteProjects.db"
import { FavoriteProjectsSchema } from "components/project/favoriteProjects/favoriteProjects.schema"
import ProjectDb from "components/project/project.db"
import { ProjectSchemaWithId } from "components/project/project.schema"
import ShiftsDb from "components/project/shifts/shifts.db"
import { ShiftsSchemaWithId } from "components/project/shifts/shifts.schema"
import UsersDb from "components/users/users.db"
import { UsersSchemaWithId } from "components/users/users.schema"
import { WeatherSchema } from "components/weather/weather.schema"
import isEqual from "lodash/isEqual"
import sortBy from "lodash/sortBy"
import uniq from "lodash/uniq"
import { DateTime } from "luxon"
import Vue from "vue"
import Vuex from "vuex"

// eslint-disable-next-line import/no-internal-modules
import { DailySchemaWithId } from "./daily/daily.schema"
// eslint-disable-next-line import/no-internal-modules
import { WorklogsSchemaWithId } from "./daily/worklogs/worklogs.schema"
import { getAllChildAreasNoDb, isAreaTreeMembershipChange } from "./project/areas/areas.utils"

// Keyed by project id. Lets two concurrent triggers of queryAndReplaceProjectAreas (e.g. the
// explicit project-switch dispatch and the server's SSE self-echo of the same switch) collapse
// into a single areas fetch instead of firing duplicate readChildren batches. A caller that joins
// still gets a result for the current root ids: the fetch re-reads if they changed while it ran.
const areasFetchInFlight = new Map<number, Promise<boolean>>()

// Keyed by project id. A project save fans out into several queryAndReplaceProject triggers
// (the explicit post-save dispatch plus the server's SSE echo of every project write), so a call
// that lands while one is running is folded into a single trailing re-run instead of a full
// duplicate fetch - the re-run still happens so a write that landed mid-fetch isn't missed.
const projectFetchInFlight = new Map<number, Promise<void>>()
const projectFetchRerunRequested = new Set<number>()

// Id of the project whose dependent data (tags, shifts, areas, admin flag) this session last fetched.
// A project restored from the offline cache (restoreCachedState) never went through fetchAndReplaceProject,
// so its dependent data may be stale and the first fetch for it must not be skipped as "unchanged".
let lastFetchedProjectId: number | undefined
// NOTE project-derived dispatches below are fire-and-forget - this tracks which ones last failed so a
// later refresh of the same, otherwise-unchanged project retries them instead of skipping forever.
// Keyed by `${projectId}:${dependency}` so a slow result for a project we've since switched away from
// can't clear (or set) the retry state of the project that's current now.
const failedProjectDependencies = new Set<string>()

/**
 * Normalizes the get*AuthUrl action payload, which callers pass as either a bare query string or an AuthUrlRequest
 *
 * @param request Query string, or query plus an optional redirect guard
 * @returns The query and a redirect guard that defaults to always redirecting
 */
function toAuthUrlRequest(request: string | AuthUrlRequest): Required<AuthUrlRequest> {
    if (typeof request === "string") return { query: request, shouldRedirect: () => true }
    return { query: request.query ?? "", shouldRedirect: request.shouldRedirect ?? (() => true) }
}

/**
 * Shared tail of the get*AuthUrl actions: bail on a failed or superseded request, otherwise send the user to the auth URL
 *
 * @param url The auth URL response, or the HTTP error from fetching it
 * @param shouldRedirect Redirect guard from the request
 * @param openEmbedded When given, handles the URL instead of navigating (embedded webview has no page navigation)
 * @returns What happened to the request
 */
function redirectToAuthUrl(url: string | HttpError, shouldRedirect: () => boolean, openEmbedded?: (url: string) => void): AuthUrlResult {
    if (isHttpError(url)) return "failed"
    if (!shouldRedirect()) return "superseded"
    if (openEmbedded) {
        openEmbedded(url)
        return "embedded"
    }

    window.location.href = url
    return "redirecting"
}

type UserPreferences = {
    compactMode: boolean
    darkMode: boolean
    stickyMode: boolean
    isActivityListView: boolean
    isConstraintListView: boolean
    isReviewListView: boolean
    isMilestoneListView: boolean
    isTimelineListView: boolean
    isWorklogListView: boolean
}

const defaultState = () => {
    return {
        hub: undefined as HubSchemaWithId | undefined,
        company: undefined as CompaniesSchemaWithId | undefined,
        user: undefined as UsersSchemaWithId | undefined,
        project: undefined as ProjectSchemaWithId | undefined,
        favoriteProjects: [] as Array<ProjectSchemaWithId>,
        projectShifts: [] as Array<ShiftsSchemaWithId>,
        projectAreas: [] as Array<AreasSchemaWithId>,
        projectAreaSequence: [] as Array<number>,
        isProjectAreaSequenceLoaded: false,
        allCompaniesInHub: [] as Array<CompaniesSchemaWithId>,
        allUsers: [] as Array<UsersSchemaWithId>,
        userProjects: [] as Array<ProjectSchemaWithId>,
        userHubs: [] as Array<HubSchemaWithId>,
        projectWorklogTags: [] as string[],
        isEmbedded: false,
        forceShowAuth: false,
        isAppOffline: false,
        isOfflineFeatureEnabled: false,
        // per-outage override - true once the user has picked "Stay Online" for the CURRENT
        // outage, so a sticky isOfflineFeatureEnabled from an earlier outage this session can't
        // force enhanced offline mode back on before they get to answer the next prompt.
        userDeclinedOfflineMode: false,
        // read-routing, this only gates whether data gets written to SQLite in the background.
        isOfflineCachingEnabled: false,
        showOfflineModePrompt: false,
        needsOfflineCacheClear: false,
        worklogsSyncTick: 0,
        showInactiveHubsProjects: false,
        isPrinting: false,
        isUserProjectAdmin: false,
        isUserSuperAdmin: false,
        isUserTeamMember: false,
        isUserHubAdmin: false,
        isUserStandardsAdmin: false,
        isUserImpersonator: false,
        loaded: false,
        // Set true as soon as hydrate() confirms a valid session, before the rest of the user/project/
        // company/areas hydration cascade runs - lets SSE connect in parallel with that cascade instead
        // of waiting behind it (the backend only requires a valid session cookie to accept /sse anyway).
        sessionConfirmed: false,
        // Monotonic - incremented on every project switch (see pickHub.client.base.ts), never
        // reset to a "seen" value. Consumers compare against their own last-seen count (!==,
        // not >) rather than watching a boolean, so a switch landing between two reads of this
        // value can't be silently dropped, and a clearState() reset back to 0 (sign-out, 401/403)
        // can't cause a stale consumer to stop reacting.
        projectSwitchCount: 0,
        pageOffSetLimit: 500,
        showCamera: false,
        uploadData: {
            uploadUrl: "",
            uploadingTo: {
                component: "",
                id: 0
            }
        },
        cachedWeatherData: {} as Record<string, WeatherSchema>,
        cachedCurrentDaily: null as DailySchemaWithId | null,
        cachedWorklogs: [] as WorklogsSchemaWithId[],
        weatherCacheTimestamp: 0,
        pendingUploads: [] as Array<PendingUpload>,
        showAudioRecorder: false,
        audioRecorderUploadUrl: "",
        isBrowserSupported: true,
        browserName: "",

        lastWorkingDailyDate: DateTime.utc().toISODate(),
        lastViewedDailyDateForOffline: null as string | null,
        userPreferences: {
            compactMode: false,
            darkMode: false,
            stickyMode: true,
            isActivityListView: false,
            isConstraintListView: false,
            isReviewListView: false,
            isMilestoneListView: false,
            isTimelineListView: false,
            isWorklogListView: false
        } as UserPreferences
    }
}

Vue.use(Vuex)

let hydrateInFlight: Promise<boolean> | null = null

/** Protest Vuex Store */
export const store = new Vuex.Store({
    state: {
        ...defaultState()
    },
    mutations: {
        setLoaded(state, loaded: boolean): void {
            state.loaded = loaded
        },
        setSessionConfirmed(state, sessionConfirmed: boolean): void {
            state.sessionConfirmed = sessionConfirmed
        },
        setIsAppOffline(state, isOffline: boolean): void {
            const wasOffline = state.isAppOffline
            state.isAppOffline = isOffline
            LOGGER.info("[STORE] setIsAppOffline", { isOffline, wasOffline, isOfflineFeatureEnabled: state.isOfflineFeatureEnabled })

            // connectivity restored - clear any pending prompt so the next loss event can prompt again
            if (!isOffline) {
                state.showOfflineModePrompt = false
                return
            }

            // prompt on every genuine online -> offline transition, regardless of whether
            // Offline Mode was already enabled in an earlier outage this session
            if (!wasOffline) {
                LOGGER.info("[STORE] Showing offline mode prompt", {})
                state.showOfflineModePrompt = true
                // fresh outage - clear any decision made during a previous one so the user gets
                // asked (and answered) again instead of inheriting the last outage's choice
                state.userDeclinedOfflineMode = false
            }
        },
        setOfflineFeatureEnabled(state, value: boolean): void {
            state.isOfflineFeatureEnabled = value
        },
        setUserDeclinedOfflineMode(state, value: boolean): void {
            state.userDeclinedOfflineMode = value
        },
        setOfflineCachingEnabled(state, value: boolean): void {
            state.isOfflineCachingEnabled = value
        },
        incrementWorklogsSyncTick(state): void {
            state.worklogsSyncTick++
        },
        setShowOfflineModePrompt(state, value: boolean): void {
            state.showOfflineModePrompt = value
        },
        setForceShowAuth(state, value: boolean): void {
            state.forceShowAuth = value
        },
        setNeedsOfflineCacheClear(state, value: boolean): void {
            state.needsOfflineCacheClear = value
        },
        setCachedWeatherData(state, weatherData: { key: string; data: WeatherSchema }): void {
            Vue.set(state.cachedWeatherData, weatherData.key, weatherData.data)
            state.weatherCacheTimestamp = Date.now()
        },
        setLastViewedDailyDateForOffline(state, date: string) {
            state.lastViewedDailyDateForOffline = date
        },

        clearWeatherCache(state): void {
            state.cachedWeatherData = {}
            state.weatherCacheTimestamp = 0
        },
        setPageOffSetLimit(state, pageOffSetLimit: number): void {
            state.pageOffSetLimit = pageOffSetLimit
        },
        setProject(state, project: ProjectSchemaWithId): void {
            state.project = project
        },
        setFavoriteProjects(state, projects: ProjectSchemaWithId[]): void {
            state.favoriteProjects = projects
        },
        setProjectShifts(state, shifts: ShiftsSchemaWithId[]): void {
            state.projectShifts = shifts
        },
        setCompany(state, company: CompaniesSchemaWithId): void {
            state.company = company
        },
        setUser(state, user: UsersSchemaWithId): void {
            state.user = user
        },
        setHub(state, hub: HubSchemaWithId | undefined): void {
            state.hub = hub
        },
        setAllCompaniesInHub(state, companies: CompaniesSchemaWithId[]): void {
            state.allCompaniesInHub = companies
        },
        setAllUsers(state, users: UsersSchemaWithId[]): void {
            state.allUsers = users
        },
        setUserProjects(state, projects: ProjectSchemaWithId[]): void {
            state.userProjects = projects
        },
        setUserHubs(state, hubs: HubSchemaWithId[]): void {
            state.userHubs = hubs
        },
        setCachedCurrentDaily(state, daily: DailySchemaWithId | null): void {
            state.cachedCurrentDaily = daily
        },
        setCachedWorklogs(state, worklogs: WorklogsSchemaWithId[]): void {
            state.cachedWorklogs = worklogs
        },
        clearState(state): void {
            const projectSwitchCount = state.projectSwitchCount
            Object.assign(state, defaultState())
            // Preserved across the reset: it must never revisit a value a still-mounted consumer's
            // lastSeen already holds, or a switch right after sign-in could go undetected.
            state.projectSwitchCount = projectSwitchCount
        },
        setEmbedded(state, isEmbedded: boolean): void {
            state.isEmbedded = isEmbedded
        },
        restoreCachedState(state, cachedState) {
            LOGGER.info("Restoring cached state", { cachedState })
            state.user = cachedState.user
            state.hub = cachedState.hub
            state.company = cachedState.company
            state.project = cachedState.project
            state.favoriteProjects = cachedState.favoriteProjects || []
            state.projectShifts = cachedState.projectShifts || []
            state.projectAreas = cachedState.projectAreas || []
            state.projectAreaSequence = cachedState.projectAreaSequence || []
            state.isProjectAreaSequenceLoaded = cachedState.isProjectAreaSequenceLoaded || false
            state.allCompaniesInHub = cachedState.allCompaniesInHub || []
            state.allUsers = cachedState.allUsers || []
            state.userProjects = cachedState.userProjects || []
            state.userHubs = cachedState.userHubs || []
            state.projectWorklogTags = cachedState.projectWorklogTags || []
            state.isUserProjectAdmin = cachedState.isUserProjectAdmin || false
            state.isUserSuperAdmin = cachedState.isUserSuperAdmin || false
            state.isUserTeamMember = cachedState.isUserTeamMember || false
            state.isUserHubAdmin = cachedState.isUserHubAdmin || false
            state.isUserStandardsAdmin = cachedState.isUserStandardsAdmin || false
            state.isUserImpersonator = cachedState.isUserImpersonator || false
            state.loaded = cachedState.loaded || true
            // A cached user implies a previously-valid session - preserve the same SSE-trigger
            // condition this path relied on before (user.id becoming truthy).
            state.sessionConfirmed = Boolean(cachedState.user)
            state.userPreferences = cachedState.userPreferences || {
                compactMode: false,
                darkMode: false,
                stickyMode: true,
                isActivityListView: false,
                isConstraintListView: false,
                isReviewListView: false,
                isMilestoneListView: false,
                isTimelineListView: false,
                isWorklogListView: false
            }

            if (cachedState.lastWorkingDailyDate) state.lastWorkingDailyDate = cachedState.lastWorkingDailyDate.slice(0, 10)
            if (cachedState.lastViewedDailyDateForOffline) state.lastViewedDailyDateForOffline = cachedState.lastViewedDailyDateForOffline
        },
        setPrinting(state, isPrinting: boolean): void {
            state.isPrinting = isPrinting
        },
        setProjectWorklogTags(state, tags: string[]): void {
            state.projectWorklogTags = tags
        },
        setProjectAreas(state, areas: AreasSchemaWithId[]): void {
            state.projectAreas = areas
        },
        incrementProjectSwitchCount(state): void {
            state.projectSwitchCount++
        },
        setProjectAreaSequence(state, areas: number[]): void {
            state.projectAreaSequence = areas
            state.isProjectAreaSequenceLoaded = true
        },
        setIsProjectAreaSequenceLoaded(state, isLoaded: boolean): void {
            state.isProjectAreaSequenceLoaded = isLoaded
        },
        setIsUserProjectAdmin(state, isAdmin: boolean): void {
            state.isUserProjectAdmin = isAdmin
        },
        setShowInactiveHubsProjects(state, isInactive: boolean): void {
            state.showInactiveHubsProjects = isInactive
        },
        setisUserSuperAdmin(state, isAdmin: boolean): void {
            state.isUserSuperAdmin = isAdmin
        },
        setisUserTeamMember(state, isTeamMember: boolean): void {
            state.isUserTeamMember = isTeamMember
        },
        setIsUserImpersonator(state, isImpersonator: boolean): void {
            state.isUserImpersonator = isImpersonator
        },
        setIsUserHubAdmin(state, isAdmin: boolean): void {
            state.isUserHubAdmin = isAdmin
        },
        setIsUserStandardsAdmin(state, isAdmin: boolean): void {
            state.isUserStandardsAdmin = isAdmin
        },
        setDarkMode(state, darkMode): void {
            state.userPreferences.darkMode = darkMode
        },
        setIsTimelineListView(state, isListView): void {
            state.userPreferences.isTimelineListView = isListView
        },
        setIsActivityListView(state, isListView): void {
            state.userPreferences.isActivityListView = isListView
        },
        setIsConstraintListView(state, isListView): void {
            state.userPreferences.isConstraintListView = isListView
        },
        setIsReviewListView(state, isListView): void {
            state.userPreferences.isReviewListView = isListView
        },
        setIsMilestoneListView(state, isListView): void {
            state.userPreferences.isMilestoneListView = isListView
        },
        setIsWorklogListView(state, isListView): void {
            state.userPreferences.isWorklogListView = isListView
        },
        setLastDailyDate(state, lastWorkingDate): void {
            state.lastWorkingDailyDate = lastWorkingDate
        },

        setStickyMode(state, stickyMode): void {
            state.userPreferences.stickyMode = stickyMode
        },
        setShowCamera(state, value): void {
            state.showCamera = value
        },
        setUploadData(state, uploadData: UploadData): void {
            state.uploadData = uploadData
        },
        setShowAudioRecorder(state, value): void {
            state.showAudioRecorder = value
        },
        setAudioRecorderUploadUrl(state, uploadUrl: string): void {
            state.audioRecorderUploadUrl = uploadUrl
        },
        setBrowserName(state, value): void {
            state.browserName = value
        },
        setBrowserSupport(state, value): void {
            state.isBrowserSupported = value
        },
        setCompactMode(state, compactMode): void {
            state.userPreferences.compactMode = compactMode
        },
        removePendingUpload(state, filepathIdentifier: string): void {
            const index = state.pendingUploads.findIndex((x) => x.filepathIdentifier === filepathIdentifier)
            if (index === -1) return
            state.pendingUploads.splice(index, 1)
        },
        addPendingUpload(state, pendingUpload: PendingUpload): void {
            const index = state.pendingUploads.findIndex((x) => isEqual(pendingUpload, x))
            if (index !== -1) return
            state.pendingUploads.push(pendingUpload)
        }
    },
    actions: {
        showCamera(state, uploadData: UploadData): void {
            state.commit("setUploadData", uploadData)
            state.commit("setShowCamera", true)
        },
        showAudioRecorder(state, uploadData: UploadData): void {
            state.commit("setUploadData", uploadData)
            state.commit("setShowAudioRecorder", true)
        },
        clearState(store): void {
            store.commit("clearState")
        },
        async queryAndReplaceHub(state, hubId: number | undefined): Promise<void> {
            if (hubId === 0 || !hubId) {
                state.commit("setHub", undefined)
                state.dispatch("determineIfHubAdmin")
                state.dispatch("determineIfStandardsAdmin")
                state.dispatch("queryForAllCompaniesInHub")
                return
            }

            const hubResponse = await HubDb.read(hubId)

            if (isHttpError(hubResponse)) {
                LOGGER.error("[STORE] Failed to read hub during query/replace.", { error: hubResponse, hubId, userId: state.state.user?.id ?? -1 })
                return
            }

            if (hubResponse.length === 0) return

            state.commit("setHub", hubResponse[0])
            state.dispatch("determineIfHubAdmin")
            state.dispatch("determineIfStandardsAdmin")
            state.dispatch("queryForAllCompaniesInHub")
        },
        async queryAndReplaceProject(state, projectId: number): Promise<void> {
            if (projectId === 0) return

            const existingFetch = projectFetchInFlight.get(projectId)
            if (existingFetch) {
                projectFetchRerunRequested.add(projectId)
                return existingFetch
            }

            const fetchPromise = (async () => {
                do {
                    projectFetchRerunRequested.delete(projectId)
                    await state.dispatch("fetchAndReplaceProject", projectId)
                } while (projectFetchRerunRequested.has(projectId))
            })()

            projectFetchInFlight.set(projectId, fetchPromise)
            try {
                await fetchPromise
            } finally {
                projectFetchInFlight.delete(projectId)
                projectFetchRerunRequested.delete(projectId)
            }
        },
        async fetchAndReplaceProject(state, projectId: number): Promise<void> {
            const projectResponse = await ProjectDb.read(projectId)

            if (isHttpError(projectResponse)) {
                LOGGER.error("[STORE] Failed to read project during query/replace.", {
                    error: projectResponse,
                    projectId,
                    userId: state.state.user?.id ?? -1
                })

                state.dispatch("clearState")
                return
            }

            if (projectResponse.length === 0) return

            const newProject = projectResponse[0]
            const previousProject = state.state.project
            const isSameProject = previousProject !== undefined && previousProject.id === newProject.id && lastFetchedProjectId === newProject.id

            const userCompany = newProject.data.companies_in_project.find((x) =>
                x.users.some((x) => x.id === state.state.user?.id && x.active === true)
            )

            if (userCompany && (!isSameProject || state.state.company?.id !== userCompany.company_id)) {
                const companyResponse = await CompaniesDb.read(userCompany.company_id)
                if (isHttpError(companyResponse)) {
                    LOGGER.error("Failed to read user company during query/replace.", {
                        error: companyResponse,
                        projectId,
                        companyId: userCompany.company_id,
                        userId: state.state.user?.id ?? -1
                    })

                    return
                }

                state.commit("setCompany", companyResponse[0])
            }

            state.commit("setProject", newProject)
            lastFetchedProjectId = newProject.id

            // Only refetch the project's dependent data when the fields it derives from actually changed, or
            // the last attempt for that dependency failed - an unrelated project write (e.g. a company logo)
            // shouldn't reload dailies, shifts and areas, but a transient failure must still be retried
            const changed = (pick: (project: ProjectSchemaWithId) => unknown, dependency: string): boolean =>
                !isSameProject || !isEqual(pick(previousProject), pick(newProject)) || failedProjectDependencies.has(`${newProject.id}:${dependency}`)

            // NOTE these dispatches are intentionally not awaited here so fetchAndReplaceProject isn't
            // blocked on them; trackDependency observes their result afterwards to update retry state.
            // A thrown error (e.g. network failure) counts as a failure so it's retried too.
            const trackDependency = (dependency: string, dispatched: Promise<boolean>): void => {
                const dependencyKey = `${newProject.id}:${dependency}`
                void dispatched
                    .catch((error: unknown) => {
                        LOGGER.error("[STORE] Project dependency refresh threw.", { error, projectId: newProject.id, dependency })
                        return false
                    })
                    .then((succeeded) => {
                        if (succeeded) failedProjectDependencies.delete(dependencyKey)
                        else failedProjectDependencies.add(dependencyKey)
                    })
            }

            if (changed((project) => project.data.companies_in_project, "projectAdmin"))
                trackDependency("projectAdmin", state.dispatch("determineIfProjectAdmin"))

            if (changed((project) => project.data.daily_ids, "worklogTags"))
                trackDependency("worklogTags", state.dispatch("queryForAllProjectWorklogTags"))

            if (changed((project) => project.data.shift_ids, "shifts")) trackDependency("shifts", state.dispatch("queryAndReplaceProjectShifts"))

            state.dispatch("determineIfCompanyTeamMember")

            if (changed((project) => project.data.areas, "areas")) trackDependency("areas", state.dispatch("queryAndReplaceProjectAreas"))
        },
        async determineIfImpersonator(store): Promise<void> {
            const isImpersonator = await http.get<boolean>(`${process.env.FLYPAPER_API_URL}/auth/isImpersonator`)
            if (isHttpError(isImpersonator)) return
            store.commit("setIsUserImpersonator", isImpersonator)
        },
        async determineIfProtestAdmin(store): Promise<void> {
            const isSuperAdmin = await http.get<boolean>(`${process.env.FLYPAPER_API_URL}/auth/isSuperAdmin`)
            if (isHttpError(isSuperAdmin)) return
            store.commit("setisUserSuperAdmin", isSuperAdmin)
        },
        async determineIfCompanyTeamMember(store): Promise<void> {
            if (!store.state.company) return
            if (!store.state.project) return
            if (!store.state.user) return

            const companyIndex = store.state.project.data.companies_in_project.findIndex((company) => company.company_id === store.state.company?.id)
            if (companyIndex === -1) return
            const userIndex = store.state.project.data.companies_in_project[companyIndex].users.findIndex((user) => user.id === store.state.user?.id)
            if (userIndex === -1) return
            const isTeamMember = store.state.project.data.companies_in_project[companyIndex].users[userIndex].roles.includes("team member")
            store.commit("setisUserTeamMember", isTeamMember)
        },

        async determineIfProjectAdmin(store): Promise<boolean> {
            if (!store.state.project) return true

            const isProjectAdmin = await http.get<boolean>(`${process.env.FLYPAPER_API_URL}/project/is/user/admin/${store.state.project.id}`)

            if (isHttpError(isProjectAdmin)) return false

            store.commit("setIsUserProjectAdmin", isProjectAdmin)
            return true
        },
        async determineIfHubAdmin(store): Promise<void> {
            if (!store.state.hub) {
                store.commit("setIsUserHubAdmin", false)
                return
            }

            const isHubAdmin = await http.get<boolean>(`${process.env.FLYPAPER_API_URL}/hub/is/user/admin/${store.state.hub.id}`)

            if (isHttpError(isHubAdmin)) {
                store.commit("setIsUserHubAdmin", false)
                return
            }

            store.commit("setIsUserHubAdmin", isHubAdmin)
        },
        async determineIfStandardsAdmin(store): Promise<void> {
            if (!store.state.hub) {
                store.commit("setIsUserStandardsAdmin", false)
                return
            }

            const isStandardsAdmin = await http.get<boolean>(`${process.env.FLYPAPER_API_URL}/hub/is/user/standardsAdmin/${store.state.hub.id}`)

            if (isHttpError(isStandardsAdmin)) {
                store.commit("setIsUserStandardsAdmin", false)
                return
            }

            store.commit("setIsUserStandardsAdmin", isStandardsAdmin)
        },
        async queryAndReplaceProjectShifts(store): Promise<boolean> {
            const project = store.state.project
            if (!project || project.data.shift_ids.length === 0) {
                store.commit("setProjectShifts", [])
                return true
            }

            const shiftResponse = await ShiftsDb.readAll({
                filter: {
                    id: project.data.shift_ids
                }
            })

            if (isHttpError(shiftResponse)) {
                store.commit("setProjectShifts", [])
                return false
            }

            store.commit("setProjectShifts", shiftResponse)
            return true
        },
        async queryAndReplaceCompany(state, companyId: number): Promise<void> {
            // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
            if (companyId === undefined || companyId === 0) return

            const companyResponse = await CompaniesDb.read(companyId)
            if (isHttpError(companyResponse)) return

            if (companyResponse.length === 0) return

            state.commit("setCompany", companyResponse[0])
            state.dispatch("determineIfCompanyAdmin")
        },
        async replaceCurrentUser({ state, commit, dispatch }, user: UsersSchemaWithId): Promise<void> {
            if (!state.user) return
            if (user.data.current_project !== state.user.data.current_project) {
                dispatch("queryForAllProjects")
                dispatch("queryAndReplaceProject", user.data.current_project)
            }

            if (user.data.current_hub !== state.user.data.current_hub) {
                dispatch("queryForAllHubs")
                dispatch("queryAndReplaceHub", user.data.current_hub)
            }

            if (user.id === state.user.id) commit("setUser", user)
        },
        async updateOrAddToAllUsers({ state, commit, dispatch }, user: UsersSchemaWithId): Promise<void> {
            const allUsers = [...state.allUsers]
            const index = allUsers.findIndex((x) => x.id === user.id)
            if (index >= 0) allUsers.splice(index, 1, user)
            else allUsers.push(user)

            commit("setAllUsers", allUsers)
        },
        async queryAndReplaceUser({ state, commit, dispatch }, userId: number): Promise<boolean> {
            LOGGER.info("[STORE] queryAndReplaceUser: start", { userId })

            // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
            if (userId === undefined || userId === 0) {
                LOGGER.warn("[STORE] queryAndReplaceUser: called with no userId", { userId })
                return false
            }

            const userResponse = await UsersDb.read(userId)
            if (isHttpError(userResponse)) {
                LOGGER.error("[STORE] Could not set user during login.", { error: userResponse, userId })
                return false
            }

            if (userResponse.length === 0) {
                LOGGER.error("[STORE] queryAndReplaceUser: UsersDb.read returned no records", { userId })
                return false
            }

            const user = userResponse[0]
            LOGGER.info("[STORE] queryAndReplaceUser: user record fetched", {
                userId: user.id,
                currentHub: user.data.current_hub,
                currentProject: user.data.current_project,
                existingStateHub: state.hub?.id,
                existingStateProject: state.project?.id
            })

            commit("setUser", user)

            const conditionalPromises = []
            if (user.data.current_project !== state.project?.id)
                conditionalPromises.push(dispatch("queryAndReplaceProject", user.data.current_project))
            if (user.data.current_hub !== state.hub?.id) conditionalPromises.push(dispatch("queryAndReplaceHub", user.data.current_hub))

            await Promise.all([
                dispatch("determineIfProtestAdmin"),
                dispatch("determineIfImpersonator"),
                dispatch("queryForAllCompaniesInHub"),
                dispatch("queryForAllUsers"),
                dispatch("queryForAllProjects"),
                dispatch("queryForAllHubs"),
                dispatch("queryForFavoriteProjects"),
                ...conditionalPromises
            ])

            LOGGER.info("[STORE] queryAndReplaceUser: complete", {
                userId,
                resultingHub: state.hub?.id,
                resultingProject: state.project?.id
            })

            return true
        },
        async queryForAllProjectWorklogTags({ state, commit }): Promise<boolean> {
            if (!state.project) return true

            const dailies = await DailyDb.readAll({
                filter: {
                    id: state.project.data.daily_ids
                }
            })

            if (isHttpError(dailies)) return false

            const worklogIds = dailies.reduce((worklogIds, daily) => {
                worklogIds.push(...daily.data.worklog_ids)
                return worklogIds
            }, [] as number[])

            const worklogs = await WorklogsDb.readAll({
                filter: {
                    id: worklogIds
                }
            })

            if (isHttpError(worklogs)) return false

            const tags = worklogs.reduce((tags, worklog) => {
                tags.push(...worklog.data.tags)
                return tags
            }, [] as string[])

            commit("setProjectWorklogTags", uniq(tags))
            return true
        },
        async queryForAllCompaniesInHub({ state, commit }): Promise<void> {
            if (!state.hub) return

            const allCompaniesInHub = await CompaniesDb.readAll({
                filter: {
                    id: state.hub.data.companies
                }
            })

            if (isHttpError(allCompaniesInHub)) {
                LOGGER.error("[STORE] Failed to get all companies in hub.", { error: allCompaniesInHub, userId: state.user?.id ?? -1 })
                return
            }

            commit("setAllCompaniesInHub", allCompaniesInHub)
        },
        async queryForAllUsers(state): Promise<void> {
            const allUsers = await UsersDb.readAll()
            if (isHttpError(allUsers)) return

            state.commit("setAllUsers", allUsers)
        },
        async queryForAllProjects(state): Promise<void> {
            const userProjects = await ProjectDb.readAll()
            if (isHttpError(userProjects)) return

            state.commit(
                "setUserProjects",
                sortBy(userProjects, (x) => x.data.name)
            )
        },
        async queryForFavoriteProjects(state): Promise<void> {
            // get the FavouriteProject data for current User
            const favoriteProjectsData: HttpError | (FavoriteProjectsSchema & RequiredId)[] = await favoriteprojectDb.readAll({
                filter: {
                    "data.user": this.state.user?.id
                }
            })

            if (isHttpError(favoriteProjectsData)) return

            if (Object.keys(favoriteProjectsData).length) {
                const projectIds = favoriteProjectsData[0].data.projects
                const Projects = await ProjectDb.readAll({
                    filter: {
                        id: projectIds
                    }
                })

                if (isHttpError(Projects)) return
                state.commit("setFavoriteProjects", Projects)
            }
        },
        async queryAndReplaceProjectAreas(state, options?: { fresh?: boolean }): Promise<boolean> {
            if (!state.state.project) return true

            const targetProjectId = state.state.project.id

            // NOTE a caller that just wrote areas passes fresh: a fetch already in flight may have read the tree
            // before that write landed, so wait it out instead of joining it. Any fetch started after it
            // settles began after the write and is safe to join below.
            if (options?.fresh) {
                const staleFetch = areasFetchInFlight.get(targetProjectId)
                if (staleFetch) await staleFetch
                // the project changed while waiting - whichever refresh is now current fetches its own areas
                // widened: TS keeps the pre-await narrowing, but the project can be cleared/switched during the await
                const currentProject = state.state.project as ProjectSchemaWithId | undefined
                if (currentProject?.id !== targetProjectId) return true
            }

            const existingFetch = areasFetchInFlight.get(targetProjectId)
            if (existingFetch) return existingFetch

            const fetchPromise = (async (): Promise<boolean> => {
                state.commit("setIsProjectAreaSequenceLoaded", false)

                let rootAreaIds: number[]
                let allAreasResults: Awaited<ReturnType<typeof AreasDb.readChildren>>[]
                do {
                    // NOTE a copy, so the comparison below sees the root ids this read started with even if
                    // the project's array is mutated in place mid-fetch
                    rootAreaIds = [...(state.state.project?.data.areas ?? [])]
                    if (rootAreaIds.length === 0) {
                        if (state.state.project?.id === targetProjectId) {
                            state.commit("setProjectAreas", [])
                            state.commit("setProjectAreaSequence", [])
                        }

                        return true
                    }

                    allAreasResults = await Promise.all(rootAreaIds.map((x: number) => AreasDb.readChildren(x, "parent_area")))

                    // NOTE the project changed again mid-fetch - this result is stale, not a failure, and
                    // whichever refresh is now current will have already kicked off its own areas fetch
                    if (state.state.project?.id !== targetProjectId) return true

                    // A newer project refresh changed the root ids while this read ran, and its areas dispatch
                    // joined this fetch instead of starting its own - re-read so that caller isn't handed a
                    // result missing the new roots (which nothing would retry, since its ids now look unchanged)
                } while (!isEqual(rootAreaIds, state.state.project.data.areas))

                const projectAreas: AreasSchemaWithId[] = []
                for (const areas of allAreasResults) {
                    if (isHttpError(areas)) {
                        LOGGER.error("[STORE] Failed to fetch project areas; leaving previous areas/sequence in place.", {
                            error: areas,
                            userId: state.state.user?.id ?? -1
                        })

                        // Restore the flag so consumers waiting on it (PlanScan's activity view-model
                        // rebuild, areasTree's buildAreaTree) don't stall forever on the unchanged
                        // previous areas/sequence we're leaving in place.
                        state.commit("setIsProjectAreaSequenceLoaded", true)
                        return false
                    }

                    projectAreas.push(...areas)
                }

                state.commit("setProjectAreas", projectAreas)

                // Derive the ordered id sequence from the data we just fetched instead of
                // re-walking the tree with one network read per node (getAllChildAreas).
                const areaDetails = projectAreas.map((area) => ({ id: area.id, childrenIds: area.data.sub_areas }))
                const allAreaIds: number[] = []
                for (const parentArea of rootAreaIds) {
                    allAreaIds.push(parentArea)
                    allAreaIds.push(...getAllChildAreasNoDb(parentArea, areaDetails))
                }

                state.commit("setProjectAreaSequence", allAreaIds)
                return true
            })()

            areasFetchInFlight.set(targetProjectId, fetchPromise)
            try {
                return await fetchPromise
            } finally {
                areasFetchInFlight.delete(targetProjectId)
            }
        },
        async updateProjectAreaIfNeeded(state, data: AreasSchemaWithId): Promise<void> {
            if (!state.state.project) return

            // queryAndReplaceProject only refetches areas when the root ids change, so a sub-area
            // added/removed under an existing area has to be picked up here
            if (isAreaTreeMembershipChange(state.state.projectAreas, state.state.project.data.areas, data)) {
                await state.dispatch("queryAndReplaceProjectAreas")

                // queryAndReplaceProjectAreas joins a fetch that's already in flight, which may have read the
                // tree before this write landed (an area save fires a burst of SSEs) - re-check against what it loaded
                // widened: TS keeps the pre-await narrowing, but the project can be cleared/switched during the await
                const currentProject = state.state.project as ProjectSchemaWithId | undefined
                if (currentProject && isAreaTreeMembershipChange(state.state.projectAreas, currentProject.data.areas, data))
                    await state.dispatch("queryAndReplaceProjectAreas")

                return
            }

            const index = state.state.projectAreas.findIndex((x) => x.id === data.id)
            if (index === -1) return
            const updatedProjectAreas = state.state.projectAreas.map((x) => (x.id === data.id ? data : x))
            state.commit("setProjectAreas", updatedProjectAreas)

            const areaDetails = updatedProjectAreas.map((area) => ({ id: area.id, childrenIds: area.data.sub_areas }))
            const allAreaIds: number[] = []
            for (const parentArea of state.state.project.data.areas) {
                allAreaIds.push(parentArea)
                allAreaIds.push(...getAllChildAreasNoDb(parentArea, areaDetails))
            }

            state.commit("setProjectAreaSequence", allAreaIds)
        },
        async queryForAllHubs(state): Promise<void> {
            const userHubs = await HubDb.readAll()
            if (isHttpError(userHubs)) return

            state.commit(
                "setUserHubs",
                sortBy(userHubs, (x) => x.data.name)
            )
        },
        async hydrate(store): Promise<boolean> {
            if (hydrateInFlight) {
                LOGGER.warn("[STORE] hydrate: already in progress, returning existing promise", { userId: store.state.user?.id ?? -1 })
                return hydrateInFlight
            }

            hydrateInFlight = (async () => {
                const maxAttempts = 3
                for (let attempt = 1; attempt <= maxAttempts; attempt++) {
                    const sessionResponse = await http.get<UsersSchemaWithId[]>(`${process.env.FLYPAPER_API_URL}/auth/session`)

                    if (isHttpError(sessionResponse)) {
                        if (sessionResponse.statusCode === 401) return false

                        const isTransient = !sessionResponse.statusCode || sessionResponse.statusCode >= 500
                        if (!isTransient) {
                            LOGGER.error("[STORE] hydrate: non-retryable error, aborting", { error: sessionResponse })
                            return false
                        }

                        LOGGER.warn(`[STORE] hydrate: session lookup failed on attempt ${attempt}/${maxAttempts}`, { error: sessionResponse })
                    } else if (sessionResponse.length === 0) {
                        return false
                    } else {
                        // Session is confirmed valid here - let SSE connect now, in parallel with the
                        // user/project/company/areas cascade below, instead of waiting behind it.
                        store.commit("setSessionConfirmed", true)
                        const success = await store.dispatch("queryAndReplaceUser", sessionResponse[0].id)
                        if (success) {
                            store.commit("setLoaded", true)
                            return true
                        }

                        LOGGER.warn(`[STORE] hydrate: failed to load user for session on attempt ${attempt}/${maxAttempts}`, {
                            sessionUserId: sessionResponse[0].id
                        })
                    }

                    if (attempt < maxAttempts) await new Promise((resolve) => setTimeout(resolve, attempt * 500))
                }

                LOGGER.error("[STORE] hydrate: giving up after max attempts", { maxAttempts })
                return false
            })().finally(() => {
                hydrateInFlight = null
            })

            return hydrateInFlight
        },
        async signOutBox(): Promise<void> {
            const userResponse = await http.get<void>(`${process.env.FLYPAPER_API_URL}/connections/box/oauth/signout`)
            if (isHttpError(userResponse)) return
            return
        },

        async signOutDocs(store): Promise<void> {
            await http.get<void>(`${process.env.FLYPAPER_API_URL}/connections/autodesk/oauth/signOut`)
        },

        async signOutGoogleDrive(): Promise<void> {
            await http.get<void>(`${process.env.FLYPAPER_API_URL}/connections/googleDrive/oauth/signOut`)
        },

        async getBoxAuthUrl(store, request: string | AuthUrlRequest = ""): Promise<AuthUrlResult> {
            const { query, shouldRedirect } = toAuthUrlRequest(request)
            let authorizeUrl: string
            if (store.state.isEmbedded) {
                authorizeUrl = process.env.FLYPAPER_API_URL + "/connections/box/authorizebox?redirect_uri=mobile"
            } else {
                authorizeUrl =
                    process.env.FLYPAPER_API_URL +
                    "/connections/box/authorizebox?redirect_uri=" +
                    encodeURIComponent(window.location.pathname + query)
            }

            const boxUrl = await http.get<{ url: string }>(authorizeUrl)
            if (isHttpError(boxUrl)) return "failed"

            return redirectToAuthUrl(
                boxUrl.url,
                shouldRedirect,
                store.state.isEmbedded ? (url) => window.nsWebViewInterface?.emit("startBoxAuth", url) : undefined
            )
        },

        async getBim360DocsAuthUrl(store, request: string | AuthUrlRequest = ""): Promise<AuthUrlResult> {
            const { query, shouldRedirect } = toAuthUrlRequest(request)
            const originalUrl = window.location.pathname + query
            const url = await http.get<string>(
                process.env.FLYPAPER_API_URL + "/connections/autodesk/oauth/url?originalUrl=" + encodeURIComponent(originalUrl)
            )

            return redirectToAuthUrl(url, shouldRedirect)
        },

        async getGoogleDriveAuthUrl(store, request: string | AuthUrlRequest = ""): Promise<AuthUrlResult> {
            const { query, shouldRedirect } = toAuthUrlRequest(request)
            const originalUrl = window.location.pathname + query
            const url = await http.get<string>(
                process.env.FLYPAPER_API_URL + "/connections/googleDrive/oauth/url?originalUrl=" + encodeURIComponent(originalUrl)
            )

            return redirectToAuthUrl(url, shouldRedirect)
        }
    },
    getters: {
        isStateReady(state): boolean {
            return state.user !== undefined
        },
        isEnhancedOfflineMode(state): boolean {
            // showOfflineModePrompt must also be resolved before engaging: on a fresh outage,
            // setIsAppOffline resets userDeclinedOfflineMode to false in the same mutation that
            // opens the prompt, so a sticky isOfflineFeatureEnabled from an earlier outage this
            // session would otherwise flip this true - and tear down the WebView - the instant the
            // outage starts, before the user has answered (or even seen) this outage's prompt.
            return state.isAppOffline && state.isOfflineFeatureEnabled && !state.userDeclinedOfflineMode && !state.showOfflineModePrompt
        },

        isBoxConnected(state): boolean {
            return state.user?.data.tokens.box?.access_token !== undefined
        },
        isBim360DocsConnected(state): boolean {
            return state.user?.data.tokens.bim360_docs?.access_token !== undefined
        },
        isGoogleDriveConnected(state): boolean {
            return state.user?.data.tokens.google_drive?.access_token !== undefined
        },
        currentCompanyName(state): string {
            if (state.user) {
                if (!state.company) return "Company not set."
                else return state.company.data.name || "Company not set"
            }

            return ""
        },
        currentProjectName(state): string {
            if (state.user) {
                if (state.user.data.current_project === 0) return "Project not set."
                else return state.project?.data.name ?? "Project not set"
            }

            return ""
        },
        currentProjectAreaPathNames(state): Record<string, string> {
            if (!state.project) return {}
            if (state.projectAreas.length === 0) return {}
            const buildAreaNamePath = (parentId: number, path: string): string => {
                if (parentId === -1) return path
                const parentArea = state.projectAreas.find((x) => x.id === parentId)
                if (!parentArea) return path
                const updatedPath = parentArea.data.title + " > " + path
                const grandParent = parentArea.data.parent_area
                if (grandParent === -1) return updatedPath
                return buildAreaNamePath(grandParent, updatedPath)
            }

            return state.projectAreas.reduce((areaPaths, area) => {
                const areaPath = area.data.title
                areaPaths[area.id] = buildAreaNamePath(area.data.parent_area, areaPath)

                return areaPaths
            }, {} as Record<string, string>)
        },
        currentProjectAreaDetails(state): Array<{
            title: string
            areaPath: string
            id: number
            childrenIds: number[]
            parentId: number
        }> {
            if (!state.project) return []
            if (state.projectAreas.length === 0) return []
            const buildAreaNamePath = (parentId: number, path: string): string => {
                if (parentId === -1) return path
                const parentArea = state.projectAreas.find((x) => x.id === parentId)
                if (!parentArea) return path
                const updatedPath = parentArea.data.title + " > " + path
                const grandParent = parentArea.data.parent_area
                if (grandParent === -1) return updatedPath
                return buildAreaNamePath(grandParent, updatedPath)
            }

            const res: { title: string; areaPath: string; id: number; childrenIds: number[]; parentId: number }[] = []
            state.projectAreas.forEach((area) => {
                if (area.data.title) {
                    res.push({
                        title: area.data.title,
                        areaPath: buildAreaNamePath(area.data.parent_area, area.data.title),
                        id: area.id,
                        childrenIds: area.data.sub_areas,
                        parentId: area.data.parent_area
                    })
                }
            })

            return res
        },
        currentHubName(state): string {
            if (state.user) {
                if (state.user.data.current_hub === 0) return "Hub Not Set"
                else return state.hub?.data.name ?? "Hub Not Set"
            }

            return ""
        },

        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        loggerState(state): any {
            return {
                user: state.user,
                hub: state.hub,
                project: state.project,
                company: state.company
            }
        },
        showPickHub(state): boolean {
            return false
        },
        noHubs(state): boolean {
            if (!state.user) return false

            return state.userHubs.length === 0
        },
        noProjects(state): boolean {
            if (!state.user) return false

            return state.userProjects.length === 0
        },
        projectHasShifts(state, getters): boolean {
            return state.projectShifts.length > 0
        },
        projectShiftIds(state): number[] {
            return state.project ? state.project.data.shift_ids : []
        }
    }
})
